import React from "react";
import { motion } from "motion/react";
import { 
  CheckCircle, 
  History, 
  Leaf, 
  Bookmark, 
  Sparkles, 
  RefreshCw, 
  Zap, 
  ShieldCheck 
} from "lucide-react";

interface AccountBenefitsProps {
  onSignInClick?: () => void;
  className?: string;
}

export default function AccountBenefits({ onSignInClick, className = "" }: AccountBenefitsProps) {
  const benefits = [
    {
      title: "Save scan history",
      desc: "Access all previous plant identifications anytime.",
      icon: <History className="w-5 h-5 text-emerald-500" />
    },
    {
      title: "Personal Plant Collection",
      desc: "Build your own saved plant library.",
      icon: <Leaf className="w-5 h-5 text-green-500" />
    },
    {
      title: "Favorite Plants & Articles",
      desc: "Bookmark useful plants and guides.",
      icon: <Bookmark className="w-5 h-5 text-amber-500" />
    },
    {
      title: "AI Care Tracking",
      desc: "Receive personalized care insights.",
      icon: <Sparkles className="w-5 h-5 text-rose-500" />
    },
    {
      title: "Multi-device Sync",
      desc: "Access your saved data anywhere.",
      icon: <RefreshCw className="w-5 h-5 text-blue-500 font-bold" />
    },
    {
      title: "Faster Experience",
      desc: "Continue from previous activity instantly.",
      icon: <Zap className="w-5 h-5 text-teal-500" />
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`bg-gradient-to-b from-gray-50 to-gray-150 border border-gray-200 rounded-[32px] p-8 md:p-10 shadow-sm relative overflow-hidden ${className}`}
    >
      {/* Background visual graphics */}
      <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-bl from-green-100/30 to-transparent rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -left-20 w-80 h-80 bg-gradient-to-tr from-emerald-100/20 to-transparent rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-4xl mx-auto space-y-8 select-none relative z-10">
        
        {/* Header Block */}
        <div className="text-center space-y-3 max-w-xl mx-auto">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100/60 rounded-full border border-green-200 text-green-800 text-[11px] font-bold uppercase tracking-wider shadow-3xs">
            <ShieldCheck className="w-3.5 h-3.5 text-green-600" /> Secure Premium Sync
          </div>
          <h2 className="text-2xl md:text-3xl font-[900] text-gray-950 tracking-tight leading-tight">
            Why create a free Plant Identifer account?
          </h2>
          <p className="text-xs md:text-sm text-gray-600 font-medium leading-relaxed">
            Create a free account to save your plant scans, build your botanical collection, sync across devices, and access personalized AI recommendations.
          </p>
        </div>

        {/* Benefits Cards Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {benefits.map((benefit, idx) => (
            <motion.div
              key={idx}
              whileHover={{ y: -3, scale: 1.015 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
              className="bg-white rounded-2xl p-5 border border-gray-150 shadow-3xs flex flex-col justify-between hover:border-green-200 hover:shadow-xs transition-colors group"
            >
              <div className="space-y-3 text-left">
                <div className="w-10 h-10 rounded-xl bg-gray-50 flex items-center justify-center border border-gray-150/40 group-hover:bg-green-50/40 transition-colors">
                  {benefit.icon}
                </div>
                <div className="space-y-1">
                  <h3 className="font-bold text-xs md:text-sm text-gray-900 tracking-tight flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                    {benefit.title}
                  </h3>
                  <p className="text-[11px] md:text-xs text-gray-500 font-medium leading-relaxed">
                    {benefit.desc}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA Button Block inside component */}
        {onSignInClick && (
          <div className="pt-3 text-center border-t border-gray-150/50 mt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
            <span className="text-xs text-gray-400 font-semibold uppercase tracking-wider">Unlock Free Lifetime Access:</span>
            <button
              onClick={onSignInClick}
              className="px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white font-extrabold text-xs uppercase tracking-wider rounded-xl shadow-xs hover:shadow-md hover:-translate-y-0.5 active:translate-y-0 transition-all cursor-pointer"
            >
              Get Free Account Now
            </button>
          </div>
        )}

      </div>
    </motion.div>
  );
}
